<template>
  <div class="order-tracking" id="order-tracking">
    <div class="container">
      <div class="d-flex justify-content-center">
        <div class="w-100" style="max-width: 800px;">
          
          <!-- Header -->
          <div class="header-section text-center mb-5">
            <div class="logo mb-4">
              <img src="../assets/images/valizo_logo.jpg" alt="VALiZO" class="logo-img">
            </div>
          </div>

          <!-- Order Search -->
          <div v-if="!orderData && !loading && !error" class="search-section">
            <Step>
              <div class="text-center">
                <h2 class="fs-24 font-smoothing mb-4">Tjek Din Ordre</h2>
                <div class="search-form">
                  <input 
                    v-model="searchToken" 
                    @keyup.enter="searchOrder"
                    type="text" 
                    class="form-control mb-3 p-3 fs-14" 
                    placeholder="Indtast dit ordre sporingslink eller token"
                    style="border: 1px solid #E2E2E2; border-radius: 0;"
                  >
                  <button 
                    @click="searchOrder" 
                    class="btn btn-primary btn-search"
                    :disabled="!searchToken.trim()"
                  >
                    Søg Ordre
                  </button>
                </div>
                <p class="text-muted mt-2 fs-12">
                  Du kan indsætte det fulde sporingslink fra din email eller bare sporingskoden
                </p>
              </div>
            </Step>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center">
            <Loader/>
          </div>

          <!-- Error -->
          <div v-if="error" class="error-section">
            <Step>
              <div class="text-center">
                <h3 class="fs-20 color-red mb-3">Ordre Ikke Fundet</h3>
                <p class="color-grey-3">Tjek venligst dit ordrenummer og prøv igen.</p>
                <button @click="resetSearch" class="btn btn-secondary">Søg Igen</button>
              </div>
            </Step>
          </div>

          <!-- Messages -->
          <div v-if="showMessageAlert" class="message-alert" :class="messageType">
            <div class="message-content">
              <span class="message-text">{{ messageText }}</span>
              <button @click="showMessageAlert = false" class="message-close">×</button>
            </div>
          </div>

          <!-- Order Details -->
          <div v-if="orderData && !loading" class="order-details">
            
            <!-- Order Header -->
            <div class="order-header text-center mb-4">
              <h2 class="fs-24 font-smoothing mb-2">DIN ORDRE:</h2>
              <h2 class="fs-30 font-weight-bold color-blue-2 mb-2">{{ orderData.order_number }}</h2>
              <h3 class="fs-18 color-grey-3 mb-4">
                {{ getOrderStatusText(orderData.status) }}
              </h3>
            </div>

            <!-- Trip Details -->
            <div class="trip-section mb-3">
              <div 
                class="expandable-section" 
                @click="toggleSection('trip')"
                :class="{ 'expanded': expandedSections.trip }"
              >
                <div class="section-header">
                  <h4 class="section-title">
                    <span v-if="shouldShowReturnFlight">
                      {{ formatTripDate(orderData.booking_home.date) }}: Hjemrejse
                    </span>
                    <span v-else>
                      Rejse Information
                    </span>
                  </h4>
                  <span class="expand-icon">{{ expandedSections.trip ? '−' : '+' }}</span>
                </div>
                
                <div v-if="expandedSections.trip" class="section-content" @click.stop>
                  <div class="trip-details">
                    <!-- Return Flight Info -->
                    <div v-if="shouldShowReturnFlight" class="return-flight mt-4">
                      <h5 class="fs-16 font-weight-bold mb-2">Returfly:</h5>
                      <div v-if="orderData.booking_home.flight_number" class="detail-row">
                        <span class="label">Fly:</span>
                        <span class="value">{{ orderData.booking_home.flight_number }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="label">Returdato:</span>
                        <span class="value">{{ orderData.booking_home.date }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="label">Returtid:</span>
                        <span class="value">{{ orderData.booking_home.time }}</span>
                      </div>
                      <div v-if="orderData.booking_home.departure_airport" class="detail-row">
                        <span class="label">Afrejselufthavn:</span>
                        <span class="value">{{ orderData.booking_home.departure_airport }}</span>
                      </div>
                      <div v-if="orderData.booking_home.transit_stops" class="detail-row">
                        <span class="label">Transitstop:</span>
                        <span class="value">{{ orderData.booking_home.transit_stops }}</span>
                      </div>
                      <div v-if="orderData.booking_home.luggage_lock_code" class="detail-row">
                        <span class="label">Bagagelås Kode:</span>
                        <span class="value">{{ orderData.booking_home.luggage_lock_code }}</span>
                      </div>
                      
                      <!-- Travelers Info -->
                      <div v-if="orderData.booking_home.travelers" class="travelers-info mt-3">
                        <h6 class="fs-14 font-weight-bold mb-2">Antal Rejsende:</h6>
                        <div v-if="orderData.booking_home.travelers.under_16" class="detail-row">
                          <span class="label">Under 16 år:</span>
                          <span class="value">{{ orderData.booking_home.travelers.under_16 }}</span>
                        </div>
                        <div v-if="orderData.booking_home.travelers.age_16" class="detail-row">
                          <span class="label">16 år:</span>
                          <span class="value">{{ orderData.booking_home.travelers.age_16 }}</span>
                        </div>
                        <div v-if="orderData.booking_home.travelers.age_17" class="detail-row">
                          <span class="label">17 år:</span>
                          <span class="value">{{ orderData.booking_home.travelers.age_17 }}</span>
                        </div>
                        <div v-if="orderData.booking_home.travelers.age_18_plus" class="detail-row">
                          <span class="label">18+ år:</span>
                          <span class="value">{{ orderData.booking_home.travelers.age_18_plus }}</span>
                        </div>
                        <div class="detail-row font-weight-bold">
                          <span class="label">Rejsende I Alt:</span>
                          <span class="value">{{ orderData.booking_home.travelers.total }}</span>
                        </div>
                      </div>
                      
                      <div v-if="orderData.booking_home.comment" class="detail-row">
                        <span class="label">Kommentar:</span>
                        <span class="value">{{ orderData.booking_home.comment }}</span>
                      </div>
                    </div>
                    
                    <!-- Message when return flight not available -->
                    <div v-if="!shouldShowReturnFlight && orderData.include_home === false" class="return-not-available mt-4">
                      <div class="info-message">
                        <p class="mb-2"><strong>Hjemrejse:</strong></p>
                        <p class="color-grey-3 fs-14">Hjemrejse er ikke tilgængelig for den valgte destination, da den ikke tillader toldservice.</p>
                        <p class="color-grey-3 fs-14">Vi kan tilbyde at hjælpe med at transportere din bagage hjem når du lander i København igen.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Luggage Info -->
            <div class="luggage-section mb-3">
              <div 
                class="expandable-section" 
                @click="toggleSection('luggage')"
                :class="{ 'expanded': expandedSections.luggage }"
              >
                <div class="section-header">
                  <h4 class="section-title">Bagage info & Fotos</h4>
                  <span class="expand-icon">{{ expandedSections.luggage ? '−' : '+' }}</span>
                </div>
                
                <div v-if="expandedSections.luggage" class="section-content" @click.stop>
                  <div v-if="orderData.people && orderData.people.length > 0" class="people-luggage">
                    <table class="luggage-table">
                      <thead>
                        <tr>
                          <th>Navn (Ansvarlig for bagagen)</th>
                          <th>Total Bagage</th>
                          <th>Total Stor Bagage</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="responsiblePerson">
                          <td>{{ responsiblePerson.first_name }} {{ responsiblePerson.last_name }}</td>
                          <td>{{ totalLuggage.small }}</td>
                          <td>{{ totalLuggage.large }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p v-if="orderData.people.length > 1" class="responsible-note">
                      <small class="color-grey-3">
                        Total baggage fra {{ orderData.people.length }} rejsende - {{ responsiblePerson.first_name }} {{ responsiblePerson.last_name }} er ansvarlig for bagagen
                      </small>
                    </p>
                  </div>
                  
                  <!-- Photo Upload Section -->
                  <div v-if="responsiblePersonWithTotalLuggage" class="luggage-photos-section mt-4">
                    <h5 class="fs-16 font-weight-bold mb-3">Bagage Fotos & Stregkoder</h5>
                    <p class="fs-12 color-grey-3 mb-4">Upload et foto ad gangen. Når du har valgt et billede, skal du blive på siden indtil uploaden er færdig.</p>
                    
                    <SimpleLuggageUpload
                      :key="`responsible-person`"
                      :person="responsiblePersonWithTotalLuggage"
                      :person-index="responsiblePersonIndex"
                      :order-token="uploadOrderToken"
                      :photos="getAllPhotosForPerson(responsiblePersonIndex)"
                      @photoUploaded="onPhotoUploaded"
                      @photoDeleted="onPhotoDeleted"
                      @show-message="showMessage"
                      @view-photo="openPhotoModal"
                    />
                  </div>
                  
                  <div v-if="orderData.luggage.airshell || orderData.luggage.plasticbag" class="additional-items mt-3">
                    <h5 class="fs-16 font-weight-bold mb-2">Ekstra Genstande:</h5>
                    <div v-if="orderData.luggage.airshell" class="detail-row">
                      <span class="label">Airshell:</span>
                      <span class="value">{{ orderData.luggage.airshell }}</span>
                    </div>
                    <div v-if="orderData.luggage.plasticbag" class="detail-row">
                      <span class="label">Plastikposer:</span>
                      <span class="value">{{ orderData.luggage.plasticbag }}</span>
                    </div>
                  </div>
                  
                  <div v-if="orderData.luggage.description" class="luggage-description mt-3">
                    <h5 class="fs-16 font-weight-bold mb-2">Bagage Beskrivelse:</h5>
                    <p class="color-grey-3">{{ orderData.luggage.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Customer Information -->
            <div class="customer-section mb-3">
              <div 
                class="expandable-section" 
                @click="toggleSection('customer')"
                :class="{ 'expanded': expandedSections.customer }"
              >
                <div class="section-header">
                  <h4 class="section-title">Kunde Information</h4>
                  <span class="expand-icon">{{ expandedSections.customer ? '−' : '+' }}</span>
                </div>
                
                <div v-if="expandedSections.customer" class="section-content" @click.stop>
                  <div class="customer-details">
                    <div class="detail-row">
                      <span class="label">Navn:</span>
                      <span class="value">{{ orderData.contact.first_name }} {{ orderData.contact.last_name }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="label">Email:</span>
                      <span class="value">{{ orderData.contact.email }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="label">Telefon:</span>
                      <span class="value">{{ orderData.contact.phone }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="label">Adresse:</span>
                      <span class="value">{{ orderData.contact.address }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="label">By:</span>
                      <span class="value">{{ orderData.contact.zip }} {{ orderData.contact.city }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="label">Betalingsmetode:</span>
                      <span class="value">{{ orderData.payment.type }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="label">Totalt Beløb:</span>
                      <span class="value">{{ orderData.payment.amount }} DKK</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>



          </div>
        </div>
      </div>
    </div>

    <!-- Photo Modal -->
    <div v-if="showPhotoModal" class="photo-modal" @click="closePhotoModal">
      <div class="photo-modal-content" @click.stop>
        <div class="photo-modal-header">
          <h4 class="photo-modal-title">{{ modalPhotoTitle }}</h4>
          <button class="photo-modal-close" @click="closePhotoModal">×</button>
        </div>
        <div class="photo-modal-body">
          <img 
            :src="modalPhotoUrl" 
            alt="Photo" 
            class="modal-image"
            :class="{ 'zoomed': imageZoomed }"
            @dblclick="toggleImageZoom"
          />
          <p v-if="!imageZoomed" class="zoom-hint">Dobbeltklik for at zoome</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Step from '../components/Step.vue'
import Loader from '../components/Loader.vue'
import SimpleLuggageUpload from '../components/SimpleLuggageUpload.vue'

export default {
  name: 'OrderTracking',
  components: {
    Step,
    Loader,
    SimpleLuggageUpload
  },
  data() {
    return {
      searchToken: '',
      orderData: null,
      loading: false,
      error: false,
      expandedSections: {
        trip: true,
        luggage: true,
        customer: false
      },
      photos: {},
      showMessageAlert: false,
      messageText: '',
      messageType: 'success',
      messageTimeout: null,
      showPhotoModal: false,
      modalPhotoUrl: '',
      modalPhotoTitle: '',
      imageZoomed: false,
      resolvedOrderToken: ''
    }
  },
  mounted() {
    if (this.$route.params.orderNumber) {
      this.searchToken = this.$route.params.orderNumber
      this.searchOrder()
    }
    
    // Add keyboard event listener for ESC key
    document.addEventListener('keydown', this.handleKeyDown)
  },

  beforeDestroy() {
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleKeyDown)
    
    // Restore body scroll if modal was open
    if (this.showPhotoModal) {
      document.body.style.overflow = 'auto'
    }
  },

  computed: {
    responsiblePerson() {
      if (!this.orderData || !this.orderData.people || !this.orderData.responsible_person_id) {
        return (this.orderData && this.orderData.people && this.orderData.people[0]) || null
      }
      
      // Find responsible person by ID (1-based index from frontend)
      const responsibleIndex = this.orderData.responsible_person_id - 1
      return this.orderData.people[responsibleIndex] || this.orderData.people[0]
    },
    
    totalLuggage() {
      if (!this.orderData || !this.orderData.people) {
        return { small: 0, large: 0 }
      }
      
      return this.orderData.people.reduce((total, person) => {
        return {
          small: total.small + (parseInt(person.luggage_small) || 0),
          large: total.large + (parseInt(person.luggage_large) || 0)
        }
      }, { small: 0, large: 0 })
    },
    
    responsiblePersonIndex() {
      if (!this.orderData || !this.orderData.responsible_person_id) {
        return 0
      }
      
      // Convert from 1-based ID to 0-based index
      return this.orderData.responsible_person_id - 1
    },
    
    responsiblePersonWithTotalLuggage() {
      if (!this.responsiblePerson) return null
      
      return {
        ...this.responsiblePerson,
        luggage_small: this.totalLuggage.small,
        luggage_large: this.totalLuggage.large
      }
    },

    shouldShowReturnFlight() {
      return this.orderData && 
             this.orderData.include_home && 
             this.orderData.booking_home && 
             typeof this.orderData.booking_home === 'object' && 
             !Array.isArray(this.orderData.booking_home);
    },

    uploadOrderToken() {
      return this.resolvedOrderToken || (this.orderData && this.orderData.secure_token) || ''
    }
  },

  methods: {
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.showPhotoModal) {
        this.closePhotoModal()
      }
    },

    extractTokenFromInput(input) {
      const urlMatch = input.match(/\/order\/([a-zA-Z0-9_-]+)(?:\?|$|\/)/)
      return urlMatch ? urlMatch[1] : input.trim()
    },
    
    async searchOrder() {
      if (!this.searchToken.trim()) return
      
      this.loading = true
      this.error = false
      this.orderData = null
      
      const token = this.extractTokenFromInput(this.searchToken)
      this.resolvedOrderToken = token
      
      try {
        const apiBase = process.env.VUE_APP_API_URL || 'https://backend.valizo.eu/wp-json/api'
        const response = await axios.get(`${apiBase}/secure-order/${token}`)
        
        if (response.data.success) {
          this.orderData = response.data.data
          
          if (this.orderData.people && Array.isArray(this.orderData.people)) {
            this.orderData.people = this.orderData.people.map(person => ({
              ...person,
              luggage_small: parseInt(person.luggage_small) || 0,
              luggage_large: parseInt(person.luggage_large) || 0
            }))
          }
          
          const apiPhotos = response.data.data.luggage_photos
          this.photos = this.normalizePhotosData(apiPhotos)
          this.expandedSections.trip = true
        } else {
          this.error = true
        }
      } catch (err) {
        this.error = true
      }
      
      this.loading = false
    },
    
    resetSearch() {
      this.searchToken = ''
      this.orderData = null
      this.error = false
      this.photos = {}
      this.resolvedOrderToken = ''
      this.expandedSections = {
        trip: true,
        luggage: true,
        customer: false
      }
      
      this.$router.push('/order')
    },
    
    toggleSection(section) {
      this.expandedSections[section] = !this.expandedSections[section]
    },
    
    getOrderStatusText(status) {
      const statusMap = {
        'ordered': 'ORDRE AFGIVET',
        'paid': 'ORDRE BEKRÆFTET',
        'processing': 'BEHANDLES',
        'completed': 'ORDRE LUKKET',
        'cancelled': 'ORDRE ANNULLERET'
      }
      
      return statusMap[status] || 'ORDRE STATUS UKENDT'
    },
    
    formatTripDate(dateStr) {
      if (!dateStr) return ''
      
      const parts = dateStr.split('-')
      if (parts.length === 3) {
        const day = parts[0]
        const month = parts[1]
        const year = parts[2]
        
        return `${day}.${month}.${year}`
      }
      
      return dateStr
    },
    
    getAllPhotosForPerson(personIndex) {
      const personPhotos = {}
      for (const key in this.photos) {
        if (key.startsWith(`${personIndex}_`)) {
          personPhotos[key] = this.photos[key]
        }
      }
      return personPhotos
    },

    onPhotoUploaded(data) {
      const key = `${data.personIndex}_${data.luggageType}_${data.itemIndex}_${data.photoType}`
      this.$set(this.photos, key, data.photoUrl)
    },

    onPhotoDeleted(data) {
      const key = `${data.personIndex}_${data.luggageType}_${data.itemIndex}_${data.photoType}`
      // Create a new photos object without the deleted photo
      const newPhotos = { ...this.photos }
      delete newPhotos[key]
      this.photos = newPhotos
    },
    
    showMessage(messageData) {
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout)
      }
      
      this.messageText = messageData.message
      this.messageType = messageData.type
      this.showMessageAlert = true
      
      this.messageTimeout = setTimeout(() => {
        this.showMessageAlert = false
      }, 5000)
    },

    openPhotoModal(photoData) {
      this.modalPhotoUrl = photoData.url
      this.modalPhotoTitle = photoData.title
      this.showPhotoModal = true
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    },

    closePhotoModal() {
      this.showPhotoModal = false
      this.modalPhotoUrl = ''
      this.modalPhotoTitle = ''
      this.imageZoomed = false // Reset zoom state
      
      // Restore body scroll
      document.body.style.overflow = 'auto'
    },
    
    toggleImageZoom() {
      this.imageZoomed = !this.imageZoomed
    },
    
    normalizePhotosData(apiPhotos) {
      const normalizedPhotos = {}
      
      if (!apiPhotos || typeof apiPhotos !== 'object') {
        return normalizedPhotos
      }

      for (const personIndex in apiPhotos) {
        const personData = apiPhotos[personIndex]
        if (!personData || typeof personData !== 'object') {
          continue
        }

        for (const luggageType in personData) {
          const typeData = personData[luggageType]
          if (!typeData || typeof typeData !== 'object') {
            continue
          }

          for (const itemIndex in typeData) {
            const itemData = typeData[itemIndex]
            if (!itemData || typeof itemData !== 'object') {
              continue
            }

            for (const photoType in itemData) {
              const photoUrl = itemData[photoType]
              if (photoUrl) {
                const key = `${personIndex}_${luggageType}_${itemIndex}_${photoType}`
                normalizedPhotos[key] = photoUrl
              }
            }
          }
        }
      }

      return normalizedPhotos
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;
@use '../assets/scss/media-queries' as *;

.order-tracking {
  min-height: 100vh;
  background: $grey-2;
  padding: 40px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

.header-section {
  .logo-img {
    max-height: 60px;
    width: auto;
  }
}

.search-section, .error-section {
  .form-control {
    max-width: 400px;
    margin: 0 auto;
  }
  
  .btn-search {
    background: #00A9E0;
    border: none;
    color: white;
    padding: 12px 30px;
    font-size: 16px;
    cursor: pointer;
    
    &:disabled {
      background: $grey-3;
      cursor: not-allowed;
    }
    
    &:hover:not(:disabled) {
      background: #0088b3;
    }
  }
}

.order-header {
  h2, h3 {
    margin: 0;
  }
}

.expandable-section {
  background: white;
  border: 1px solid $grey-1;
  margin-bottom: 10px;
  box-shadow: 0 2px 10px 0 rgba(47,59,80,0.1);
  cursor: pointer;
  
  &.expanded {
    .section-header {
      background: #00A9E0;
      color: white;
    }
  }
  
  .section-header {
    background: #00A9E0;
    color: white;
    padding: 20px 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .section-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
    }
    
    .expand-icon {
      font-size: 24px;
      font-weight: bold;
      width: 30px;
      height: 30px;
      background: white;
      color: #00A9E0;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  
  .section-content {
    padding: 30px;
    
    .detail-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      
      .label {
        font-weight: 600;
        color: $blue-2;
        min-width: 140px;
      }
      
      .value {
        color: $grey-3;
        text-align: right;
        flex: 1;
      }
      
      &.font-weight-bold {
        .label, .value {
          font-weight: bold;
          color: $blue-2;
        }
      }
    }
  }
}

.luggage-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid $grey-1;
  
  th, td {
    border: 1px solid $grey-1;
    text-align: left;
    padding: 15px;
  }
  
  th {
    background: $grey-2;
    font-weight: 600;
    color: $blue-2;
  }
  
  td {
    color: $grey-3;
  }
}

.btn-secondary {
  background: $grey-3;
  border: none;
  color: white;
  padding: 12px 30px;
  font-size: 16px;
  cursor: pointer;
  
  &:hover {
    background: $blue-2;
  }
}

.color-red {
  color: $red;
}

// Photo Modal Styles
.photo-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  .photo-modal-content {
    background: white;
    border-radius: 8px;
    max-width: 90vw;
    max-height: 90vh;
    overflow: hidden;
    
    .photo-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px 20px;
      border-bottom: 1px solid #E2E2E2;
      background: #00A9E0;
      color: white;
      
      .photo-modal-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
      }
      
      .photo-modal-close {
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        font-weight: bold;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        
        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
    
    .photo-modal-body {
      padding: 20px;
      text-align: center;
      
      .modal-image {
        max-width: 100%;
        max-height: 70vh;
        height: auto;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        transition: transform 0.3s ease;
        
        &.zoomed {
          max-width: none;
          max-height: none;
          transform: scale(2);
          cursor: zoom-out;
        }
        
        &:not(.zoomed) {
          cursor: zoom-in;
        }
      }

      .zoom-hint {
        margin-top: 10px;
        font-size: 12px;
        color: #888;
      }
    }
  }
}

// Bootstrap-like utility classes
.d-flex {
  display: flex;
}

.justify-content-center {
  justify-content: center;
}

.w-100 {
  width: 100%;
}

.text-center {
  text-align: center;
}

.mb-2 {
  margin-bottom: 8px;
}

.mb-3 {
  margin-bottom: 16px;
}

.mb-4 {
  margin-bottom: 24px;
}

.mb-5 {
  margin-bottom: 32px;
}

.mt-3 {
  margin-top: 16px;
}

.mt-4 {
  margin-top: 24px;
}

.mt-5 {
  margin-top: 32px;
}

.fs-16 {
  font-size: 16px;
}

.font-weight-bold {
  font-weight: bold;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid $grey-1;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #00A9E0;
  }
}

.message-alert {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  
  &.success {
    background: #d4edda;
    border: 1px solid #c3e6cb;
    
    .message-content {
      color: #155724;
    }
  }
  
  &.error {
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    
    .message-content {
      color: #721c24;
    }
  }
  
  .message-content {
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .message-text {
      flex: 1;
      font-size: 14px;
    }
    
    .message-close {
      background: none;
      border: none;
      font-size: 20px;
      font-weight: bold;
      cursor: pointer;
      margin-left: 15px;
      opacity: 0.7;
      
      &:hover {
        opacity: 1;
      }
    }
  }
}

.luggage-photos-section {
  border-top: 1px solid #E2E2E2;
  padding-top: 20px;
}

.responsible-note {
  margin-top: 15px;
  padding: 10px;
  background: #f8f9fa;
  border-left: 4px solid #00A9E0;
  border-radius: 0 4px 4px 0;
  
  small {
    font-style: italic;
  }
}

.return-not-available {
  .info-message {
    padding: 20px;
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    border-left: 4px solid #f39c12;
    
    p {
      margin-bottom: 8px;
      line-height: 1.4;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

@include max-screen(991px) {
  .expandable-section {
    .section-header {
      padding: 15px 20px;
      
      .section-title {
        font-size: 16px;
      }
    }
    
    .section-content {
      padding: 20px;
      
      .detail-row {
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 15px;
        
        .label {
          min-width: auto;
          margin-bottom: 5px;
        }
        
        .value {
          text-align: left;
        }
      }
    }
  }
  
  .luggage-table {
    font-size: 14px;
    
    th, td {
      padding: 10px 8px;
    }
  }
  
  .photo-modal {
    .photo-modal-content {
      max-width: 95vw;
      
      .photo-modal-header {
        padding: 10px 15px;
        
        .photo-modal-title {
          font-size: 14px;
        }
      }
      
      .photo-modal-body {
        padding: 15px;
        
        .modal-image {
          max-height: 60vh;
          
          &.zoomed {
            transform: scale(1.5);
          }
        }
      }
    }
  }
}
</style> 
